/**
 * Prescription drawer: an over-the-page <dialog> with three paths — upload a
 * file (OCR → pre-filled form), enter the values by hand, or defer ("add
 * later"). On submit it validates, best-effort saves through the App Proxy,
 * writes the result to the shared RX state and emits `rx:prescription-submit`.
 *
 * Ported from the OTIS theme. The Horizon dialog base and its declarative
 * bindings are replaced with a plain custom element driving a native <dialog>;
 * the High Index upsell is dropped, since ONYA prices lenses by variant rather
 * than by prescription power.
 */
import {
  getRxState,
  mapOcrPrismValues,
  parsePdMeasurement,
  validatePrescription,
  mapOcrToValues,
  isPrescriptionExpired,
  normalizeDateToIso,
  formatCents,
} from '#rx/core';
import { analyzePrescription, savePrescription } from '#rx/api';
import '#rx/select';

const ALLOWED_EXTENSIONS = ['jpg', 'jpeg', 'png', 'webp', 'heic', 'pdf'];
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'application/pdf'];

function escapeHtml(value) {
  return String(value ?? '').replace(
    /[&<>"']/g,
    (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char])
  );
}

function isFilled(value) {
  return value != null && value !== '' && value !== 'Select';
}

/**
 * Fired when the drawer collects a prescription (or the "add later" choice).
 * `detail` mirrors `RxState.prescription`. Bubbles, so a host section such as
 * my-orders can react without touching the shared PDP state.
 */
export class RxPrescriptionSubmitEvent extends CustomEvent {
  static eventName = 'rx:prescription-submit';
  constructor(detail) {
    super(RxPrescriptionSubmitEvent.eventName, { bubbles: true, detail });
  }
}

class RxPrescriptionDrawer extends HTMLElement {
  #state;
  #limits = {};
  #config = {};

  #step = 'INITIAL'; // 'INITIAL' | 'MANUAL' | 'PD_MEASURE'
  #analyzing = false;
  #submitting = false;
  #method = null; // 'upload' | 'manual' | 'later'
  #values = {};
  #uniqueId = null;
  #rxUID = null;
  #fileName = null;
  #fileUrl = null;
  #fileIsPdf = false;
  #fileError = false;
  #zoom = 1;
  #pan = { x: 0, y: 0 };
  #dragFrom = null;
  #apiError = null;
  #dualPd = false;
  #showPrism = false;
  #errors = {};

  connectedCallback() {
    this.#state = getRxState();
    this.#limits = this.#parseJson(this.dataset.limits) || {};
    this.#config = {
      subdomain: this.dataset.subdomain || '',
      providerNumber: this.dataset.providerNumber || '',
      checkExpiration: this.dataset.checkExpiration !== 'false',
      healthFundNumbers: this.dataset.healthFundNumbers || '',
      pdWidgetUrl: this.dataset.pdWidgetUrl || '',
      pdWidgetMode: this.dataset.pdWidgetMode || 'card',
      pdWidgetPrimary: this.dataset.pdWidgetPrimary || '',
      pdWidgetText: this.dataset.pdWidgetText || '',
    };

    this.addEventListener('click', this.#onClick);
    this.addEventListener('change', this.#onChange);
    this.addEventListener('pointerdown', this.#onPointerDown);
    this.addEventListener('pointermove', this.#onPointerMove);
    this.addEventListener('pointerup', this.#onPointerUp);
    this.addEventListener('pointercancel', this.#onPointerUp);
    this.addEventListener('wheel', this.#onWheel, { passive: false });
    window.addEventListener('message', this.#onWidgetMessage);
    // Opened by the configurator and by the Need RX page alike.
    document.addEventListener('rx:prescription:open', this.#onOpenRequest);

    this.#dialog?.addEventListener('click', this.#onBackdropClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('change', this.#onChange);
    document.removeEventListener('rx:prescription:open', this.#onOpenRequest);
    window.removeEventListener('message', this.#onWidgetMessage);
  }

  #parseJson(str) {
    try {
      return str ? JSON.parse(str) : null;
    } catch {
      return null;
    }
  }

  get #dialog() {
    return this.querySelector('dialog');
  }

  get #body() {
    return this.querySelector('[data-rx-body]');
  }

  // --- Entry point -----------------------------------------------------------

  // Limits and proxy config come from the drawer's own data attributes, which
  // carry the step each picker needs. The opener only asks for the drawer.
  #onOpenRequest = () => this.start();

  start() {
    this.#reset();
    this.#render();
    this.#dialog?.showModal();
  }

  #close() {
    this.#dialog?.close();
  }

  #reset() {
    this.#step = 'INITIAL';
    this.#analyzing = false;
    this.#submitting = false;
    this.#fileError = false;
    this.#apiError = null;
    this.#errors = {};

    // Keep a prescription already captured this session so it can be edited.
    const existing = this.#state?.prescription;
    if (existing && existing.method && existing.method !== 'later') {
      this.#method = existing.method;
      this.#values = { ...(existing.values || {}) };
      this.#uniqueId = existing.uniqueId ?? null;
      this.#rxUID = existing.rxUID ?? null;
      this.#dualPd = isFilled(this.#values.pd_left) || isFilled(this.#values.pd_right);
      this.#showPrism = Object.keys(this.#values).some((key) => key.startsWith('prism_'));
    } else {
      this.#method = null;
      this.#values = {};
      this.#uniqueId = null;
      this.#rxUID = null;
      this.#fileName = null;
      if (this.#fileUrl) URL.revokeObjectURL(this.#fileUrl);
      this.#fileUrl = null;
      this.#fileIsPdf = false;
      this.#resetZoom();
      this.#dualPd = false;
      this.#showPrism = false;
    }
  }

  goBack = () => {
    this.#step = this.#step === 'PD_MEASURE' ? 'MANUAL' : 'INITIAL';
    this.#errors = {};
    this.#apiError = null;
    this.#render();
  };

  // --- Event delegation ------------------------------------------------------

  #onBackdropClick = (event) => {
    // A native dialog reports clicks on its backdrop as clicks on itself.
    if (event.target === this.#dialog) this.#close();
  };

  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const zoom = target.closest('[data-rx-zoom]');
    if (zoom) {
      const action = zoom.dataset.rxZoom;
      if (action === 'reset') this.#resetZoom(), this.#applyZoom();
      else this.#setZoom(this.#zoom + (action === 'in' ? 0.5 : -0.5));
      return;
    }

    const action = target.closest('[data-rx-close]')
      ? () => this.#close()
      : target.closest('[data-rx-back]')
        ? this.goBack
        : target.closest('[data-rx-upload]')
          ? () => this.querySelector('[data-rx-file]')?.click()
          : target.closest('[data-rx-manual]')
            ? () => this.#openManual()
            : target.closest('[data-rx-later]')
              ? () => this.#addLater()
              : target.closest('[data-rx-measure-pd]')
                ? () => this.#openPdMeasure()
                : target.closest('[data-rx-submit]')
                  ? () => this.#submit()
                  : null;
    if (!action) return;

    // Deferred so the re-render does not detach the element mid-dispatch.
    setTimeout(action, 0);
  };

  #onChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    if (target.matches('[data-rx-file]')) {
      const file = target.files?.[0];
      if (file) this.#handleFile(file);
      return;
    }
    if (target.matches('[data-rx-dual-pd]')) {
      this.#collect();
      this.#dualPd = target.checked;
      this.#render();
      return;
    }
    if (target.matches('[data-rx-prism-toggle]')) {
      this.#collect();
      this.#showPrism = target.checked;
      this.#render();
      return;
    }
    if (target.matches('[data-rx-field]')) {
      const field = target.dataset.rxField;
      this.#values[field] = target.value;
      // The date drives the expiry warning — refresh it live.
      if (field === 'date') this.#renderExpiryWarning();
    }
  };

  /** Read every current field back into #values before a re-render. */
  #collect() {
    for (const el of this.querySelectorAll('[data-rx-field]')) {
      this.#values[el.dataset.rxField] = el.value;
    }
  }

  // --- Flows -----------------------------------------------------------------

  #openManual() {
    this.#step = 'MANUAL';
    this.#errors = {};
    this.#render();
  }

  // --- Online PD measurement -------------------------------------------------

  #openPdMeasure() {
    this.#collect();
    this.#step = 'PD_MEASURE';
    this.#render();
  }

  /** The widget URL with the theme's colours and the configured method. */
  #pdWidgetSrc() {
    const base = this.#config.pdWidgetUrl;
    if (!base) return '';
    try {
      const url = new URL(base, window.location.href);
      url.searchParams.set('mode', this.#config.pdWidgetMode);
      if (this.#config.pdWidgetPrimary) {
        url.searchParams.set('primaryColor', this.#config.pdWidgetPrimary);
        url.searchParams.set('lineColor', this.#config.pdWidgetPrimary);
      }
      if (this.#config.pdWidgetText) url.searchParams.set('textColor', this.#config.pdWidgetText);
      url.searchParams.set('borderRadius', '0px');
      return url.href;
    } catch {
      return '';
    }
  }

  /**
   * The widget posts with targetOrigin "*", so checking the origin here is the
   * only thing stopping any other script on the page from faking a result.
   *
   * Two shapes arrive: the iris method sends one combined `pd`, the card method
   * sends `pdRight`/`pdLeft` and no combined value at all. They are told apart
   * by which fields are present — the iris method sends no `method` field.
   */
  #onWidgetMessage = (event) => {
    const expected = this.#pdWidgetSrc();
    if (!expected) return;
    if (event.origin !== new URL(expected).origin) return;

    const measured = parsePdMeasurement(event.data);
    if (!measured) return; // nothing usable — the widget stays open

    if (measured.pdRight) {
      // Monocular halves: keep both, they are the point of the card method.
      this.#values.pd_right = measured.pdRight;
      this.#values.pd_left = measured.pdLeft;
      delete this.#values.pd;
      this.#dualPd = true;
    } else {
      this.#values.pd = measured.pd;
      delete this.#values.pd_right;
      delete this.#values.pd_left;
      this.#dualPd = false;
    }

    delete this.#errors.pd;
    this.#step = 'MANUAL';
    this.#render();
  };

  #addLater() {
    const prescription = { method: 'later', values: null, uniqueId: null, rxUID: null, expired: false };
    this.#state.set('prescription', prescription);
    this.dispatchEvent(new RxPrescriptionSubmitEvent(prescription));
    this.#close();
  }

  #validFile(file) {
    const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
    return ALLOWED_TYPES.includes(file.type) || ALLOWED_EXTENSIONS.includes(ext);
  }

  async #handleFile(file) {
    this.#fileError = false;
    if (!this.#validFile(file)) {
      this.#fileError = true;
      this.#render();
      return;
    }

    this.#method = 'upload';
    this.#fileName = file.name;
    if (this.#fileUrl) URL.revokeObjectURL(this.#fileUrl);
    this.#fileUrl = URL.createObjectURL(file);
    this.#fileIsPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
    this.#resetZoom();
    this.#analyzing = true;
    this.#apiError = null;
    this.#render();

    try {
      const result = await analyzePrescription(file);
      this.#uniqueId = result.uniqueId || null;
      this.#rxUID = result.rxUID || null;

      const details = result.analysis?.consensus?.prescriptionDetails;
      const mapped = mapOcrToValues(result.prescriptionData);

      // The flat payload carries neither the date nor any prism, so the
      // consensus has to be read alongside it.
      const detailsDate = normalizeDateToIso(details?.prescriptionDate);
      if (detailsDate) mapped.date = detailsDate;

      const prism = mapOcrPrismValues(details);
      Object.assign(mapped, prism);

      // A single PD can also be hiding in the consensus only.
      if (!isFilled(mapped.pd) && !isFilled(mapped.pd_right)) {
        const consensusPd = mapOcrToValues({ pupillaryDistance: details?.pupillaryDistance });
        if (isFilled(consensusPd.pd)) mapped.pd = consensusPd.pd;
      }

      this.#values = { ...this.#values, ...mapped };
      if (isFilled(mapped.pd_left) || isFilled(mapped.pd_right)) this.#dualPd = true;
      // Open the prism block, or the recognised values would sit there unseen.
      if (Object.keys(prism).length) this.#showPrism = true;
    } catch (error) {
      // OCR failed — fall back to manual entry with a friendly notice.
      console.error('rx: prescription analysis failed', error);
      this.#method = 'manual';
      this.#apiError = 'We could not read that file automatically. Please enter your prescription below.';
    } finally {
      this.#analyzing = false;
      this.#step = 'MANUAL';
      this.#render();
    }
  }

  async #submit() {
    this.#collect();
    const result = validatePrescription(this.#values, this.#limits, {
      checkExpiration: this.#config.checkExpiration,
    });
    this.#errors = result.errors;

    if (!result.valid) {
      this.#render();
      this.#body?.querySelector('.rx-prescription__field--error')?.scrollIntoView({ block: 'center' });
      return;
    }

    // Expiry only warns — the submit proceeds and the flag is carried on.
    this.#submitting = true;
    this.#render();

    let rxUID = this.#rxUID;
    try {
      const saved = await savePrescription(this.#buildSavePayload());
      if (saved?.rxUID) rxUID = saved.rxUID;
    } catch (error) {
      // A save failure must not block checkout — the RX can be attached later.
      console.error('rx: save prescription failed', error);
    }
    this.#rxUID = rxUID;

    const prescription = {
      method: this.#method || 'manual',
      values: { ...this.#values },
      uniqueId: this.#uniqueId,
      rxUID,
      expired: result.expired,
    };
    this.#state.set('prescription', prescription);
    this.dispatchEvent(new RxPrescriptionSubmitEvent(prescription));

    this.#submitting = false;
    this.#close();
  }

  /** Bubbling is what carries the submit to the configurator, which buys. */

  /** Shape the save-prescription payload per the backend contract. */
  #buildSavePayload() {
    const v = this.#values;
    const num = (value, fallback) => (isFilled(value) ? String(value) : fallback);

    const prescriptionData = {
      sphRight: num(v.sph_od, '0.00'),
      sphLeft: num(v.sph_os, '0.00'),
      cylRight: num(v.cyl_od, '0.00'),
      cylLeft: num(v.cyl_os, '0.00'),
      axisRight: num(v.axis_od, '000'),
      axisLeft: num(v.axis_os, '000'),
      addRight: num(v.add_od, '0.00'),
      addLeft: num(v.add_os, '0.00'),
      isMonocularPd: this.#dualPd,
    };

    if (this.#dualPd) {
      prescriptionData.pdRight = `${num(v.pd_right, '0')}mm`;
      prescriptionData.pdLeft = `${num(v.pd_left, '0')}mm`;
    } else {
      prescriptionData.pdValue = `${num(v.pd, '0')}mm`;
    }
    if (isFilled(v.date)) prescriptionData.prescriptionDate = v.date;

    if (this.#showPrism) {
      const base = { U: 'Up', D: 'Down', I: 'In', O: 'Out' };
      const eye = (side, hDefault, vDefault) => ({
        horizontal: { value: num(v[`prism_h_${side}`], '0.00'), base: base[v[`prism_hdir_${side}`]] || hDefault },
        vertical: { value: num(v[`prism_v_${side}`], '0.00'), base: base[v[`prism_vdir_${side}`]] || vDefault },
      });
      // A zero prism is not a prism: the reference treats '0.00' as empty here,
      // so an untouched prism block never reaches the lab as a real value.
      const hasPrism = ['prism_h_od', 'prism_v_od', 'prism_h_os', 'prism_v_os'].some(
        (key) => isFilled(v[key]) && v[key] !== '0.00'
      );
      if (hasPrism) {
        prescriptionData.prismFormat = 'directional';
        prescriptionData.prismRight = eye('od', 'Out', 'Up');
        prescriptionData.prismLeft = eye('os', 'Out', 'Up');
      }
    }

    return {
      order_id: `cart-temp-${Date.now()}`,
      line_item_id: `prescription-${Date.now()}`,
      subdomain: this.#config.subdomain,
      data: { prescriptionData },
    };
  }

  // --- Rendering -------------------------------------------------------------

  #render() {
    const body = this.#body;
    if (!body) return;

    const back = this.querySelector('[data-rx-back]');
    if (back instanceof HTMLElement) back.hidden = this.#step === 'INITIAL' || this.#analyzing;

    const headline = this.querySelector('[data-rx-headline]');
    if (headline instanceof HTMLElement) headline.textContent = this.#headline();

    // The confirm view with a file preview gets the wide two-column dialog.
    this.#dialog?.classList.toggle(
      'rx-prescription-drawer__dialog--wide',
      this.#step === 'MANUAL' && !this.#analyzing && Boolean(this.#fileUrl)
    );

    if (this.#analyzing) {
      body.innerHTML = this.#loaderHtml();
      return;
    }
    body.innerHTML =
      this.#step === 'PD_MEASURE'
        ? this.#pdMeasureHtml()
        : this.#step === 'MANUAL'
          ? this.#manualHtml()
          : this.#initialHtml();
    // The step is re-rendered wholesale, so re-apply the zoom the shopper set.
    if (this.#fileUrl && !this.#fileIsPdf) this.#applyZoom();
  }

  // Sits in the drawer header, so each step states its own job once.
  #headline() {
    if (this.#analyzing) return 'Reading your prescription';
    if (this.#step === 'PD_MEASURE') return 'Measure your PD';
    if (this.#step !== 'MANUAL') return 'Add prescription';
    return this.#method === 'upload' && Object.keys(this.#values).length
      ? 'Confirm your prescription'
      : 'Enter your prescription';
  }

  #loaderHtml() {
    return `
      <div class="rx-prescription__loader" role="status" aria-live="polite">
        <span class="rx-prescription__spinner" aria-hidden="true"></span>
        <p class="rx-prescription__loader-text">Reading your prescription…</p>
      </div>
    `;
  }

  #initialHtml() {
    const total = this.#state.totalPrice;
    return `
      <div class="rx-prescription__step rx-prescription__step--initial">
        <p class="rx-prescription__subtitle">Upload it and we'll read the values for you, or enter them by hand.</p>

        ${this.#fileError ? '<p class="rx-prescription__notice" role="alert">That file type is not supported. Use a JPG, PNG, WEBP, HEIC or PDF.</p>' : ''}

        <div class="rx-prescription__actions">
          <button type="button" class="btn btn--primary btn--solid btn--full rx-prescription__action" data-rx-upload>Upload prescription</button>
          <span class="rx-prescription__or">or</span>
          <button type="button" class="btn btn--secondary btn--solid btn--full rx-prescription__action" data-rx-manual>Enter it manually</button>
        </div>

        ${total ? `<div class="rx-prescription__subtotal"><span>Subtotal</span><span>${formatCents(total)}</span></div>` : ''}

        <div class="rx-prescription__later">
          <button type="button" class="rx-prescription__later-btn" data-rx-later>Add prescription later</button>
          <p class="rx-prescription__subtitle">You can attach it from your account after checkout.</p>
        </div>
      </div>
    `;
  }

  #previewHtml() {
    if (!this.#fileUrl) return '';

    // A PDF is rendered by the browser's own viewer, which brings its own
    // zoom — wrapping it in ours would fight it.
    if (this.#fileIsPdf) {
      return `
        <aside class="rx-prescription__preview">
          <embed class="rx-prescription__preview-media" src="${this.#fileUrl}" type="application/pdf">
          <p class="rx-prescription__preview-name">${escapeHtml(this.#fileName || '')}</p>
        </aside>
      `;
    }

    return `
      <aside class="rx-prescription__preview">
        <div class="rx-prescription__preview-viewport" data-rx-viewport>
          <img class="rx-prescription__preview-media" data-rx-preview-img src="${this.#fileUrl}" alt="Uploaded prescription">
        </div>
        <div class="rx-prescription__preview-bar">
          <p class="rx-prescription__preview-name">${escapeHtml(this.#fileName || '')}</p>
          <div class="rx-prescription__zoom">
            <button type="button" class="rx-prescription__zoom-btn" data-rx-zoom="out" aria-label="Zoom out">&minus;</button>
            <span class="rx-prescription__zoom-level" data-rx-zoom-level>100%</span>
            <button type="button" class="rx-prescription__zoom-btn" data-rx-zoom="in" aria-label="Zoom in">+</button>
            <button type="button" class="rx-prescription__zoom-btn" data-rx-zoom="reset" aria-label="Reset zoom">&#8634;</button>
          </div>
        </div>
      </aside>
    `;
  }

  // --- Scan zoom -------------------------------------------------------------

  #resetZoom() {
    this.#zoom = 1;
    this.#pan = { x: 0, y: 0 };
    this.#dragFrom = null;
  }

  #setZoom(next) {
    this.#zoom = Math.min(4, Math.max(1, Math.round(next * 10) / 10));
    // Back at natural size the image re-centres, so a stale pan would offset it.
    if (this.#zoom === 1) this.#pan = { x: 0, y: 0 };
    this.#applyZoom();
  }

  #applyZoom() {
    const img = this.querySelector('[data-rx-preview-img]');
    const viewport = this.querySelector('[data-rx-viewport]');
    const level = this.querySelector('[data-rx-zoom-level]');
    if (!img) return;

    if (this.#zoom > 1) {
      // Keep the image covering the viewport: panning may not expose a gap.
      const limitX = (img.clientWidth * (this.#zoom - 1)) / 2;
      const limitY = (img.clientHeight * (this.#zoom - 1)) / 2;
      this.#pan.x = Math.min(limitX, Math.max(-limitX, this.#pan.x));
      this.#pan.y = Math.min(limitY, Math.max(-limitY, this.#pan.y));
    }

    img.style.transform = `translate(${this.#pan.x}px, ${this.#pan.y}px) scale(${this.#zoom})`;
    if (level) level.textContent = `${Math.round(this.#zoom * 100)}%`;
    viewport?.classList.toggle('rx-prescription__preview-viewport--zoomed', this.#zoom > 1);

    for (const button of this.querySelectorAll('[data-rx-zoom]')) {
      const action = button.dataset.rxZoom;
      button.disabled =
        (action === 'in' && this.#zoom >= 4) ||
        (action === 'out' && this.#zoom <= 1) ||
        (action === 'reset' && this.#zoom === 1 && this.#pan.x === 0 && this.#pan.y === 0);
    }
  }

  #onPointerDown = (event) => {
    const viewport = event.target.closest?.('[data-rx-viewport]');
    if (!viewport || this.#zoom <= 1) return;
    this.#dragFrom = { x: event.clientX - this.#pan.x, y: event.clientY - this.#pan.y };
    viewport.classList.add('rx-prescription__preview-viewport--dragging');
    viewport.setPointerCapture?.(event.pointerId);
  };

  #onPointerMove = (event) => {
    if (!this.#dragFrom) return;
    this.#pan = { x: event.clientX - this.#dragFrom.x, y: event.clientY - this.#dragFrom.y };
    this.#applyZoom();
  };

  #onPointerUp = (event) => {
    this.#dragFrom = null;
    event.target.closest?.('[data-rx-viewport]')?.classList.remove('rx-prescription__preview-viewport--dragging');
  };

  #onWheel = (event) => {
    const viewport = event.target.closest?.('[data-rx-viewport]');
    if (!viewport) return;
    event.preventDefault();
    this.#setZoom(this.#zoom + (event.deltaY < 0 ? 0.2 : -0.2));
  };

  #manualHtml() {
    const filled = Boolean(this.#fileName);
    const preview = this.#previewHtml();
    return `
      <div class="rx-prescription__step${preview ? ' rx-prescription__step--split' : ''}">
      <div class="rx-prescription__form-col">
        ${filled && !preview ? `<p class="rx-prescription__file">Uploaded: ${escapeHtml(this.#fileName)}</p>` : ''}
        ${this.#apiError ? `<p class="rx-prescription__notice" role="alert">${escapeHtml(this.#apiError)}</p>` : ''}

        <div class="rx-prescription__rxcard">
          <span></span>
          <span class="rx-prescription__rxcol">SPH</span>
          <span class="rx-prescription__rxcol">CYL</span>
          <span class="rx-prescription__rxcol">Axis</span>
          <span class="rx-prescription__rxcol">Add</span>
          ${this.#eyeRowHtml('OD', 'right', 'od')}
          ${this.#eyeRowHtml('OS', 'left', 'os')}
        </div>

        <div class="rx-prescription__rule"></div>

        <div class="rx-prescription__subgrid">
          ${this.#pdHtml()}
          <div class="rx-prescription__field-group">
            <label class="rx-prescription__label" for="rx-date-${this.id}">Prescription date</label>
            <input type="date" id="rx-date-${this.id}" class="rx-prescription__input" data-rx-field="date" value="${escapeHtml(this.#values.date || '')}">
          </div>
        </div>

        <div class="rx-prescription__extras">
          <label class="rx-prescription__check">
            <input type="checkbox" data-rx-dual-pd ${this.#dualPd ? 'checked' : ''}>
            <span>Two PD values</span>
          </label>
          <label class="rx-prescription__check">
            <input type="checkbox" data-rx-prism-toggle ${this.#showPrism ? 'checked' : ''}>
            <span>I have prism values</span>
          </label>
        </div>

        ${this.#showPrism ? this.#prismHtml() : ''}
        <div data-rx-expiry>${this.#expiryWarningHtml()}</div>
      </div>
      ${preview}
      </div>

      <div class="rx-prescription__footer">
        <div class="rx-prescription__footer-summary">
          <span>Frame + lenses</span>
          <span class="rx-prescription__footer-price">${formatCents(this.#state.totalPrice)}</span>
        </div>
        <button type="button" class="btn btn--primary btn--solid btn--full rx-prescription__submit" data-rx-submit ${this.#submitting ? 'disabled aria-busy="true"' : ''}>
          ${this.#submitting ? 'Saving…' : 'Save prescription'}
        </button>
      </div>
    `;
  }

  /** One eye across the four power columns. */
  #eyeRowHtml(abbr, side, key) {
    return `
      <span class="rx-prescription__rxeye">${abbr}<small>${side}</small></span>
      ${this.#cellHtml(`sph_${key}`, 'sph', true, {})}
      ${this.#cellHtml(`cyl_${key}`, 'cyl', true, {})}
      ${this.#cellHtml(`axis_${key}`, 'axis', false, { pad: 3 })}
      ${this.#cellHtml(`add_${key}`, 'add', true, {})}
    `;
  }

  #cellHtml(field, limitKey, signed, opts) {
    const error = this.#errors[field];
    return `
      <span class="rx-prescription__field${error ? ' rx-prescription__field--error' : ''}">
        ${this.#selectHtml(field, limitKey, signed, opts)}
        ${error ? `<span class="rx-prescription__error">${escapeHtml(error)}</span>` : ''}
      </span>
    `;
  }

  #pdHtml() {
    const error = this.#errors.pd;
    return `
      <div class="rx-prescription__field-group${error ? ' rx-prescription__field--error' : ''}">
        <span class="rx-prescription__label">Pupillary distance (PD)</span>
        <div class="rx-prescription__pd-inputs">
          ${
            this.#dualPd
              ? `${this.#selectHtml('pd_right', 'pd', false, { half: true, prefix: 'R' })}
                 ${this.#selectHtml('pd_left', 'pd', false, { half: true, prefix: 'L' })}`
              : this.#selectHtml('pd', 'pd', false, {})
          }
        </div>
        ${error ? `<span class="rx-prescription__error">${escapeHtml(error)}</span>` : ''}
        ${
          this.#pdWidgetSrc()
            ? '<button type="button" class="rx-prescription__measure-pd" data-rx-measure-pd>Measure PD online</button>'
            : ''
        }
      </div>
    `;
  }

  #pdMeasureHtml() {
    const src = this.#pdWidgetSrc();
    if (!src) return '';
    const hint =
      this.#config.pdWidgetMode === 'card'
        ? 'Look into the camera and follow the prompts.'
        : 'Turn your sound on — the steps are spoken.';
    return `
      <div class="rx-prescription__step rx-prescription__step--measure">
        <p class="rx-prescription__subtitle">${hint}</p>
        <iframe
          class="rx-prescription__pd-frame"
          src="${escapeHtml(src)}"
          title="Pupillary distance measurement"
          allow="camera; autoplay"
        ></iframe>
        <button type="button" class="rx-prescription__later-btn" data-rx-back>Enter PD by hand instead</button>
      </div>
    `;
  }

  #prismHtml() {
    const eye = (side, label) => `
      <fieldset class="rx-prescription__prism-eye">
        <legend>${label}</legend>
        <div class="rx-prescription__prism-row">
          <span>Vertical</span>${this.#selectHtml(`prism_v_${side}`, 'prism', false, {})}${this.#prismDirHtml(`prism_vdir_${side}`, 'vertical')}
        </div>
        <div class="rx-prescription__prism-row">
          <span>Horizontal</span>${this.#selectHtml(`prism_h_${side}`, 'prism', false, {})}${this.#prismDirHtml(`prism_hdir_${side}`, 'horizontal')}
        </div>
      </fieldset>
    `;
    return `<div class="rx-prescription__prism">${eye('od', 'Right (OD)')}${eye('os', 'Left (OS)')}</div>`;
  }

  #prismDirHtml(field, axis) {
    const options = axis === 'vertical' ? [['U', 'Up'], ['D', 'Down']] : [['I', 'In'], ['O', 'Out']];
    return this.#selectShell(field, options, 'Base', this.#values[field] || '', {});
  }

  /**
   * Two-column value picker over the configured limit range. Columns fan out
   * from the value people start at: 0 for powers, 64 (32 monocular) for PD.
   */
  #selectHtml(field, limitKey, signed, opts = {}) {
    const current = this.#values[field];
    const options = this.#options(limitKey, signed, opts);
    const label = opts.prefix ? `<span class="rx-prescription__mini">${opts.prefix}</span>` : '';
    const anchor = this.#anchorFor(limitKey, opts);
    // PD splits by whole millimetre and half step; powers fan out from zero.
    const split = limitKey === 'pd' ? 'halves' : 'anchor';

    return label + this.#selectShell(field, options, 'Select', isFilled(current) ? String(current) : '', { anchor, split });
  }

  #selectShell(field, options, placeholder, value, { anchor = null, split = 'anchor' } = {}) {
    return `
      <rx-select
        class="rx-select"
        data-rx-field="${field}"
        data-options="${escapeHtml(JSON.stringify(options))}"
        ${anchor == null ? '' : `data-anchor="${anchor}"`}
        ${split === 'halves' ? 'data-split="halves"' : ''}
        data-placeholder="${escapeHtml(placeholder)}"
        value="${escapeHtml(value)}"
      >
        <button type="button" class="rx-select__trigger" role="combobox"
                aria-expanded="false" aria-haspopup="listbox"
                aria-label="${escapeHtml(field)}">${escapeHtml(placeholder)}</button>
        <div class="rx-select__panel" role="listbox" hidden></div>
      </rx-select>
    `;
  }

  /**
   * The value the columns fan out from. Zero for every power field — axis and
   * add start there too, they just never go negative.
   */
  #anchorFor(limitKey, opts) {
    if (limitKey === 'pd') return opts.half ? 32 : 64;
    if (limitKey === 'prism') return null;
    return 0;
  }

  #options(limitKey, signed, opts) {
    const limit = this.#limits[limitKey] || {};
    let min = Number(limit.min ?? 0);
    let max = Number(limit.max ?? 0);
    const step = Number(limit.step) > 0 ? Number(limit.step) : 1;
    if (opts.half) {
      // Monocular PD is roughly half the binocular range.
      min = Math.floor(min / 2);
      max = Math.ceil(max / 2);
    }
    const count = Math.max(0, Math.round((max - min) / step));
    const out = [];
    for (let i = 0; i <= count; i++) {
      out.push(this.#formatOption(min + i * step, signed, opts.pad));
    }
    return out;
  }

  #formatOption(n, signed, pad) {
    if (pad) return String(Math.round(n)).padStart(pad, '0');
    if (n === 0) return '0.00';
    const fixed = Math.abs(n).toFixed(2);
    if (n < 0) return `-${fixed}`;
    return signed ? `+${fixed}` : fixed;
  }

  #expiryWarningHtml() {
    if (!this.#config.checkExpiration) return '';
    if (!isPrescriptionExpired(this.#values.date)) return '';
    return `
      <p class="rx-prescription__notice rx-prescription__notice--warning" role="alert">
        This prescription looks older than 24 months. You can still continue, but we recommend an up-to-date prescription.
      </p>
    `;
  }

  #renderExpiryWarning() {
    const host = this.#body?.querySelector('[data-rx-expiry]');
    if (host) host.innerHTML = this.#expiryWarningHtml();
  }
}

if (!customElements.get('rx-prescription-drawer')) {
  customElements.define('rx-prescription-drawer', RxPrescriptionDrawer);
}
